import { useCallback, useEffect, useRef, useState } from "react";
import { fetchClientWeather } from "../api/weatherService";

/**
 * Shared Client Profile weather fetch so Weather & Hazards + Device location
 * cards share one GET /users/:id/weather (GMA-1778 / Copilot).
 *
 * Cross-client isolation: a profile can switch clients while a request is in
 * flight, and one client's location must never render under another client's
 * profile — in ANY frame. Two guards, both required:
 *  - requestRef discards completions whose request id is no longer current;
 *  - state carries forClientId and is masked SYNCHRONOUSLY at render time when
 *    it differs from the current clientId, because the effect-based reset runs
 *    post-commit and the first frame after a switch would otherwise leak the
 *    previous client's snapshot.
 * A manual reload of the SAME client keeps its data visible while refreshing.
 */
export default function useClientWeather(clientId) {
  const [state, setState] = useState({
    loading: true,
    error: null,
    data: null,
    forClientId: clientId,
  });
  const requestRef = useRef(0);

  const reload = useCallback(async () => {
    if (!clientId) return;
    const requestId = ++requestRef.current;
    setState((s) => ({ ...s, loading: true, error: null, forClientId: clientId }));
    try {
      const data = await fetchClientWeather(clientId);
      if (requestRef.current !== requestId) return; // stale completion
      setState({ loading: false, error: null, data, forClientId: clientId });
    } catch (err) {
      if (requestRef.current !== requestId) return; // stale failure
      console.error("[weather] failed to load client weather", err);
      setState({
        loading: false,
        error: "Weather is temporarily unavailable. Please try again.",
        data: null,
        forClientId: clientId,
      });
    }
  }, [clientId]);

  useEffect(() => {
    // New client (or mount): invalidate in-flight completions and reset state.
    requestRef.current += 1;
    setState({ loading: true, error: null, data: null, forClientId: clientId });
    reload();
  }, [reload, clientId]);

  // Synchronous mask: if state still belongs to a different client, this render
  // must not expose it — the effect above has not run yet in this frame.
  const isCurrent = state.forClientId === clientId;
  return {
    loading: isCurrent ? state.loading : true,
    error: isCurrent ? state.error : null,
    data: isCurrent ? state.data : null,
    reload,
  };
}
